import {Stub} from '@/api/models';
import {Feed, Feeds} from '@/api/models/Feeds.ts';
import Pending from '@/components/Pending.tsx';
import {Button, MenuItem} from '@blueprintjs/core';
import {ItemRenderer, Select} from '@blueprintjs/select';
import {ReactElement, useCallback, useMemo} from 'react';

export type FeedStub = Stub<Feed, 'name'>;
export type FeedSelectFn = (feed: Feed) => void;

interface Props {
	onSelect: FeedSelectFn,
	current: FeedStub | null,
	fill?: boolean,
}

export default function FeedSelect({onSelect, current, fill = true}: Props): ReactElement {
	const feeds = useMemo(async () => {
		const feeds = await Feeds.list();
		return feeds.sort((a, b) => a.name.localeCompare(b.name));
	}, []);

	return (
		<Pending resource={feeds}>
			{feeds => <Content feeds={feeds} current={current} fill={fill} onSelect={onSelect} />}
		</Pending>
	);
}

interface ContentProps {
	feeds: Feed[],
	current: FeedStub | null,
	onSelect: FeedSelectFn,
	fill?: boolean,
}

function Content({feeds, current, fill, onSelect}: ContentProps): ReactElement {
	const itemRenderer: ItemRenderer<Feed> = useCallback((item, {handleClick, handleFocus, modifiers}) => {
		if (!modifiers.matchesPredicate)
			return null;

		return (
			<MenuItem
				key={item.id}
				text={item.name}
				onClick={handleClick}
				onFocus={handleFocus}
				active={modifiers.active}
				selected={item.id === current?.id}
				roleStructure="listoption"
			/>
		);
	}, [current]);

	return (
		<Select
			items={feeds}
			itemRenderer={itemRenderer}
			itemPredicate={itemPredicate}
			itemsEqual="id"
			onItemSelect={onSelect}
			fill={fill}
			popoverProps={{
				matchTargetWidth: true,
			}}
		>
			<Button
				alignText="start"
				text={current ? current.name : 'Select a feed...'}
				endIcon="chevron-down"
				fill={fill}
			/>
		</Select>
	);
}

function itemPredicate(query: string, feed: Feed): boolean {
	return feed.name.toLowerCase().includes(query.toLowerCase());
}
